import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faUserTie,
  faUsers,
  faBoxArchive,
  faChartColumn,
  faCalendarDays,
  faSliders,
  faShieldHalved,
  faEnvelope,
  faMagnifyingGlass,
  faChevronDown,
  faHeartPulse,
  faComments,
  faClipboardList,
  faBook,
} from '@fortawesome/free-solid-svg-icons';
import { calendarAPI } from '../../../services/calendarService';
import './Sidebar.css';

/* ─── Role label map ──────────────────────────────────────────────────────── */
const ROLE_LABELS = {
  1: 'Super Admin',
  2: 'Admin',
  3: 'Practice Admin',
  4: 'Provider',
  5: 'Practice Caregiver',
  6: 'Patient',
  7: 'System Caregiver',
};

/* ─── Icon map ─────────────────────────────────────────────────────────────── */
const ICON_MAP = {
  'fa-cogs': faGear,
  'fa-user': faUserTie,
  'fa-users': faUsers,
  'fa-cube': faBoxArchive,
  'fa-bar-chart': faChartColumn,
  'fa-calendar': faCalendarDays,
  'fa-cog': faSliders,
  'fa-lock': faShieldHalved,
  'fa-envelope': faEnvelope,
  'fa-search': faMagnifyingGlass,
  'fa-heartbeat': faHeartPulse,
  'fa-comments': faComments,
  'fa-clipboard': faClipboardList,
};

const resolveIcon = (icon) => ICON_MAP[icon] || faGear;

/* ─── Section separator helper ─────────────────────────────────────────────── */
const SEPARATOR = { type: 'separator' };

const Sidebar = ({ user, isOpen, onClose, isMobileView = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openDropdown, setOpenDropdown] = useState(null);
  const [hasSystemCalendarViewPermission, setHasSystemCalendarViewPermission] = useState(false);

  const handleDropdown = (key) =>
    setOpenDropdown((prev) => (prev === key ? null : key));

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  /* ── Auto-open parent dropdown on route change ── */
  useEffect(() => {
    const items = getMenuItems(user?.role_id);
    const activeItem = items.find(
      (item) =>
        item.type === 'dropdown' &&
        item.children?.some(
          (child) =>
            location.pathname === child.path ||
            location.pathname.startsWith(child.path + '/')
        )
    );
    if (activeItem) setOpenDropdown(activeItem.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ── Calendar permission check ── */
  useEffect(() => {
    const check = async () => {
      if (user?.role_id !== 7 || !user?.id) {
        setHasSystemCalendarViewPermission(false);
        return;
      }
      try {
        const res = await calendarAPI
          .checkPermission(user.id, 'View')
          .catch(() => ({ hasPermission: false }));
        setHasSystemCalendarViewPermission(Boolean(res?.hasPermission));
      } catch {
        setHasSystemCalendarViewPermission(false);
      }
    };
    check();
  }, [user?.id, user?.role_id]);

  /* ── Menu definitions ── */
  function getMenuItems(roleId) {
    switch (roleId) {
      case 1: // Super Admin
      case 2: // Admin
        return [
          // ── Practice Management ──
          { type: 'section', label: 'Practice Management' },
          { path: '/practices', label: 'Practice', icon: 'fa-cogs' },
          // System Caregiver disabled
          // { path: '/system-caregiver-dashboard', label: 'Caregiver', icon: 'fa-users' },

          // ── Admin ──
          { type: 'section', label: 'Administration' },
          {
            key: 'admin-mgmt', label: 'Admin', icon: 'fa-user', type: 'dropdown',
            children: [
              { label: 'Admin List', path: '/admin' },
              { label: 'User Logs', path: '/events-log' },
            ],
          },
          { path: '/inventory', label: 'Inventory', icon: 'fa-cube' },
          { path: '/billing', label: 'Billing', icon: 'fa-bar-chart' },

          // ── Clinical ──
          { type: 'section', label: 'Clinical' },
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-users', type: 'dropdown',
            children: [
              { label: 'Look up Patient', path: '/patients/lookup' },
              { label: 'Look up Patient (By RPM Date)', path: '/patients/lookup-rpm' },
            ],
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [{ label: 'Follow up', path: '/care/follow-up' }],
          },
          // Calendar Settings disabled
          // {
          //   key: 'calendar-setting', label: 'Calendar Settings', icon: 'fa-calendar', type: 'dropdown',
          //   children: [
          //     { label: 'Visit Type', path: '/calendar-setting/visit-type' },
          //     { label: 'Working Hours', path: '/calendar-setting/working-hours' },
          //     { label: 'Calendar', path: '/calendar-setting/calendar' },
          //   ],
          // },

          // ── System ──
          { type: 'section', label: 'System' },
          { path: '/settings', label: 'Settings', icon: 'fa-cog' },
          { path: '/permission', label: 'Permissions', icon: 'fa-lock' },
          { path: '/email-logs', label: 'Email Logs', icon: 'fa-envelope' },
        ];

      case 3: // Practice Admin
        return [
          { type: 'section', label: 'Practice' },
          { path: `/practices/${user?.practice_id || ''}`, label: 'Practice', icon: 'fa-cogs' },
          { type: 'section', label: 'Clinical' },
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-users', type: 'dropdown',
            children: [
              { label: 'Add New Patient', path: `/practices/${user?.practice_id || ''}/add-patient` },
              { label: 'Look up Patient', path: '/patients/lookup' },
              { label: 'Look up Patient (By RPM Date)', path: '/patients/lookup-rpm' },
            ],
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [{ label: 'Follow up', path: '/care/follow-up' }],
          },
          // Calendar Settings disabled
          // {
          //   key: 'calendar-setting', label: 'Calendar Settings', icon: 'fa-calendar', type: 'dropdown',
          //   children: [
          //     { label: 'Visit Type', path: '/calendar-setting/visit-type' },
          //     { label: 'Working Hours', path: '/calendar-setting/working-hours' },
          //     { label: 'Calendar', path: '/calendar-setting/calendar' },
          //   ],
          // },
          { type: 'section', label: 'Communication' },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
          { type: 'section', label: 'System' },
          { path: '/settings', label: 'Settings', icon: 'fa-cog' },
        ];

      case 4: // Provider
        return [
          { type: 'section', label: 'Clinical' },
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-users', type: 'dropdown',
            children: [
              { label: 'Add New Patient', path: `/practices/${user?.practice_id}/add-patient` },
              { label: 'Look up Patient', path: '/patients/lookup' },
              { label: 'Look up Patient (By RPM Date)', path: '/patients/lookup-rpm' },
            ],
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [{ label: 'Follow up', path: '/care/follow-up' }],
          },
          // Calendar Settings disabled
          // {
          //   key: 'calendar-setting', label: 'Calendar Setting', icon: 'fa-calendar', type: 'dropdown',
          //   children: [
          //     { label: 'Visit Type', path: '/calendar-setting/visit-type' },
          //     { label: 'Working Hours', path: '/calendar-setting/working-hours' },
          //     { label: 'Calendar', path: '/calendar-setting/calendar' },
          //   ],
          // },
          { type: 'section', label: 'Communication' },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
          { type: 'section', label: 'System' },
          { path: '/settings', label: 'Settings', icon: 'fa-cog' },
        ];

      case 5: // Practice Caregiver
        return [
          { type: 'section', label: 'Clinical' },
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-users', type: 'dropdown',
            children: [
              { label: 'Add New Patient', path: `/practices/${user?.practice_id}/add-patient` },
              { label: 'Look up Patient', path: '/patients/lookup' },
              { label: 'Look up Patient (By RPM Date)', path: '/patients/lookup-rpm' },
            ],
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [{ label: 'Follow up', path: '/care/follow-up' }],
          },
          { type: 'section', label: 'Communication' },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
          { type: 'section', label: 'System' },
          { path: '/settings', label: 'Settings', icon: 'fa-cog' },
        ];

      case 6: // Patient
        return [
          { type: 'section', label: 'Account' },
          { path: '/patient-settings', label: 'Settings', icon: 'fa-cog' },
        ];

      /* System Caregiver role disabled
      case 7: { // System Caregiver
        const practiceMatch = location.pathname.match(/^\/practices\/(\d+)/);
        const currentPracticeId = practiceMatch ? practiceMatch[1] : null;

        if (currentPracticeId) {
          const items = [
            { type: 'section', label: 'Practice Management' },
            { path: '/system-caregiver-dashboard', label: 'Practices', icon: 'fa-cogs' },
            { type: 'section', label: 'Clinical' },
            {
              key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-users', type: 'dropdown',
              children: [
                { label: 'Add New Patient', path: `/practices/${currentPracticeId}/add-patient` },
                { label: 'Look up Patient', path: '/patients/lookup' },
                { label: 'Look up Patient (By RPM Date)', path: '/patients/lookup-rpm' },
              ],
            },
            {
              key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
              children: [{ label: 'Follow up', path: '/care/follow-up' }],
            },
            { type: 'section', label: 'Communication' },
            { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
            { type: 'section', label: 'System' },
            { path: '/settings', label: 'Settings', icon: 'fa-cog' },
          ];

          if (hasSystemCalendarViewPermission) {
            // Insert calendar after care-mgmt
            const careIdx = items.findIndex((i) => i.key === 'care-mgmt');
            items.splice(careIdx + 1, 0, {
              key: 'calendar-setting', label: 'Calendar Setting', icon: 'fa-calendar', type: 'dropdown',
              children: [
                { label: 'Visit Type', path: '/calendar-setting/visit-type' },
                { label: 'Calendar', path: '/calendar-setting/calendar' },
              ],
            });
          }
          return items;
        }

        // Dashboard (no practice context)
        const dashItems = [
          { type: 'section', label: 'Practice Management' },
          { path: '/system-caregiver-dashboard', label: 'Practices', icon: 'fa-cogs' },
          { type: 'section', label: 'Clinical' },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [{ label: 'Follow up', path: '/care/follow-up' }],
          },
          { path: '/patients/lookup', label: 'Look up Patient', icon: 'fa-search' },
          { path: '/patients/lookup-rpm', label: 'Lookup by RPM Date', icon: 'fa-calendar' },
          { type: 'section', label: 'Communication' },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
          { type: 'section', label: 'System' },
          { path: '/settings', label: 'Settings', icon: 'fa-cog' },
        ];

        if (hasSystemCalendarViewPermission) {
          const lookupIdx = dashItems.findIndex((i) => i.path === '/patients/lookup');
          dashItems.splice(lookupIdx, 0, {
            key: 'calendar-setting', label: 'Calendar Setting', icon: 'fa-calendar', type: 'dropdown',
            children: [
              { label: 'Visit Type', path: '/calendar-setting/visit-type' },
              { label: 'Calendar', path: '/calendar-setting/calendar' },
            ],
          });
        }
        return dashItems;
      }
      */

      default:
        return [];
    }
  }

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobileView && typeof onClose === 'function') onClose();
  };

  const menuItems = getMenuItems(user?.role_id);

  /* ── User initials for footer ── */
  const userInitials = user?.name
    ? user.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : '?';
  const roleLabel = ROLE_LABELS[user?.role_id] || 'User';

  return (
    <div
      className={`left-panel sidebar-shell ${isOpen ? 'show fullwidthPanel' : 'collapsedPanel'}`}
      id="leftmenusection"
      aria-hidden={!isOpen}
    >
      <div className="leftmenuinner">
        {/* ── Navigation ── */}
        <nav className="sidebar-navigation" aria-label="Main navigation">
          <div className="manu-section">
            <ul className="sidebar-main-list">
              {menuItems.map((item, idx) => {
                /* Section heading */
                if (item.type === 'section') {
                  return (
                    <li key={`section-${idx}`} className="sidebar-section-item" aria-hidden="true">
                      <span className="sidebar-section-label">{item.label}</span>
                    </li>
                  );
                }

                /* Dropdown group */
                if (item.type === 'dropdown') {
                  const childActive = item.children.some((c) => isActive(c.path));
                  const open = openDropdown === item.key;

                  return (
                    <li
                      key={item.key}
                      className={[
                        'sidebar-dropdown',
                        open ? 'sidebar-dropdown-open' : '',
                        childActive ? 'sidebar-dropdown-child-active' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div
                        className={`sidebar-group-title${open || childActive ? ' active' : ''}`}
                        onClick={() => handleDropdown(item.key)}
                        role="button"
                        aria-expanded={open}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleDropdown(item.key)}
                      >
                        <span className="sidebar-title">
                          <span className="nav-icon">
                            <FontAwesomeIcon icon={resolveIcon(item.icon)} />
                          </span>
                          <span className="nav-label">{item.label}</span>
                        </span>
                        <span className="dropdown-arrow" aria-hidden="true">
                          <FontAwesomeIcon icon={faChevronDown} />
                        </span>
                      </div>

                      {open && (
                        <ul className="sidebar-submenu">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <a
                                href={child.path}
                                className={isActive(child.path) ? 'active' : ''}
                                onClick={(e) => { e.preventDefault(); handleNavigate(child.path); }}
                              >
                                {child.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                /* Leaf link */
                const activeClass = isActive(item.path) ? 'active' : '';
                return (
                  <li key={item.path}>
                    <a
                      href={item.path}
                      className={activeClass}
                      title={item.label}
                      onClick={(e) => { e.preventDefault(); handleNavigate(item.path); }}
                    >
                      <span className="nav-icon">
                        <FontAwesomeIcon icon={resolveIcon(item.icon)} />
                      </span>
                      <span className="nav-label">{item.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {/* ── Footer: user pill ── */}
        {user && (
          <div className="sidebar-footer">

            {/* Version + Environment */}
            <div className="dropdown-footer-row dropdown-footer-version">
              <span className="dropdown-footer-badge">Version 1.0.8 - {process.env.NODE_ENV === 'production' ? 'Production' : 'Development'}</span>
            </div>

            <div className="dropdown-footer-row dropdown-footer-links">
              <a
                href="/assets/codexterlabs-User-Manual.pdf" /*DOCUMENTATION*/
                target="_blank"
                rel="noopener noreferrer"
                className="dropdown-footer-action"
                title="Documentation"
              >
                <FontAwesomeIcon icon={faBook} className="footer-action-icon" />
                <span className="footer-action-text">Documentation</span>
              </a>
              <a
                href="mailto:support.vitarpm@codexterlabs.com"
                className="dropdown-footer-action"
                title="Support"
              >
                <FontAwesomeIcon icon={faEnvelope} className="footer-action-icon" />
                <span className="footer-action-text">Support</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
